import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Send, Edit3, Save, X, CheckCircle2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { AiUpgradeCard } from "@/components/merchant/AiUpgrade";

/**
 * AI Coupon Assistant (Phase 1) — conversational coupon drafting.
 * The full existing manual coupon flow is untouched; this is a separate path.
 */
export default function CouponAssistant() {
  const { loading, aiEnabled } = useAiEntitlement();
  if (loading) return <div className="p-8 text-sm text-slate-500" data-testid="ai-assistant-loading">Loading…</div>;
  if (!aiEnabled) return <div className="max-w-3xl mx-auto py-8"><AiUpgradeCard feature="The AI Coupon Assistant" testid="ai-assistant-locked" /></div>;
  return <CouponAssistantInner />;
}

function CouponAssistantInner() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSid = searchParams.get("sid");
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState({});
  const [needs, setNeeds] = useState([]);
  const [ready, setReady] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef(null);

  // Resume the given session if `?sid=` is present, otherwise start fresh.
  // The GET endpoint doesn't return `products`/`categories` so we fetch them
  // separately when resuming to keep parity with a brand-new session.
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        if (resumeSid) {
          const [{ data: sess }, { data: fresh }] = await Promise.all([
            api.get(`/ai-coupons/${resumeSid}`),
            api.post("/ai-coupons/session").catch(() => ({ data: {} })),
          ]);
          if (cancelled) return;
          // Reuse the resumed session but keep the catalogue lookups
          setSession({
            ...sess,
            categories: fresh.categories || [],
            products: fresh.products || [],
          });
          setDraft(sess.draft || {});
        } else {
          const { data } = await api.post("/ai-coupons/session");
          if (cancelled) return;
          setSession(data);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to start assistant");
      }
    };
    bootstrap();
    return () => { cancelled = true; };
  }, [resumeSid]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [session?.messages]);

  // "Ask AI to change" chips — shown once the draft has meaningful content.
  // Clicking pre-fills the chat input so the merchant can tweak the phrasing
  // before sending. Chips adapt to the current draft so we never suggest
  // an edit that doesn't apply.
  const changeChips = useMemo(() => {
    const chips = [];
    if (draft.discount_type === "percent") {
      chips.push("Make it 20% instead");
      chips.push("Change to a $10 flat discount");
    } else if (draft.discount_type === "fixed") {
      chips.push("Make it $25 instead");
      chips.push("Switch to a percentage discount");
    }
    if (draft.expires_at) {
      chips.push("Extend by another week");
      chips.push("Shorten it to 3 days");
    }
    if ((draft.provinces || []).length > 0 || (draft.cities || []).length > 0) {
      chips.push("Also add British Columbia");
      chips.push("Make it online only across Canada");
    } else if (draft.online_only) {
      chips.push("Limit to Ontario only");
    }
    if (draft.category_id) {
      chips.push("Change the category");
    }
    if ((draft.product_ids || []).length > 0) {
      chips.push("Add another product");
      chips.push("Remove one of the products");
    } else if ((session?.products || []).length > 0) {
      chips.push("Only apply to one specific product");
    }
    if (draft.title) {
      chips.push("Rewrite the title to feel more urgent");
    }
    return chips.slice(0, 4);
  }, [draft, session?.products]);

  const askChange = (phrase) => setInput(phrase);

  if (!session) {
    return <div className="p-8 text-slate-600" data-testid="coupon-ai-loading">Starting AI assistant…</div>;
  }

  const send = async (text) => {
    if (!text.trim() || sending) return;
    setSending(true);
    const optimistic = {
      ...session,
      messages: [...session.messages, { role: "user", content: text, at: new Date().toISOString() }],
    };
    setSession(optimistic);
    setInput("");
    try {
      const { data } = await api.post(`/ai-coupons/${session.id}/message`, { message: text });
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: "assistant", content: data.reply, at: new Date().toISOString() }],
      }));
      setDraft(data.draft || {});
      setNeeds(data.needs || []);
      setReady(!!data.ready_to_publish);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is unavailable");
    } finally {
      setSending(false);
    }
  };

  const publish = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/ai-coupons/${session.id}/publish`);
      if (data.ok) {
        toast.success("Coupon published!");
        nav("/merchant/coupons");
      } else {
        toast.error(data.message || "Publish failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setSending(false); }
  };

  const saveDraft = async () => {
    await api.post(`/ai-coupons/${session.id}/save-draft`);
    toast.success("Draft saved");
    nav("/merchant/coupons");
  };

  const cancel = async () => {
    await api.post(`/ai-coupons/${session.id}/cancel`).catch(() => {});
    nav("/merchant/coupons");
  };

  const editManually = async () => {
    await api.post(`/ai-coupons/${session.id}/edit-manually`).catch(() => {});
    sessionStorage.setItem("ai_coupon_draft", JSON.stringify(draft));
    nav("/merchant/coupons?from_ai=1");
  };

  const suggestions = [
    "Give 15% off running shoes for 7 days in Ontario",
    "Create $20 off orders over $100 online in Canada",
    "Weekend promotion: 20% off jackets in Toronto and Mississauga",
  ];

  return (
    <div data-testid="coupon-assistant" className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="pf-card p-0 overflow-hidden flex flex-col" style={{ height: "calc(100vh - 12rem)" }}>
        <div className="px-5 py-4 border-b border-white/60 flex items-center gap-2">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Assistant</div>
            <div className="font-display text-lg text-slate-900">Create a coupon</div>
          </div>
          <button onClick={cancel} className="pf-btn-glass px-3 py-1.5 text-sm" data-testid="ai-cancel-btn">
            <X className="w-4 h-4 inline" /> Cancel
          </button>
        </div>

        <div ref={scroller} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {session.messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-slate-900 text-white"
                  : "bg-white/85 border border-slate-200 text-slate-900"}`}
                   data-testid={`ai-msg-${m.role}-${i}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl text-sm bg-white/85 border border-slate-200 text-slate-500">
                Thinking…
              </div>
            </div>
          )}
        </div>

        {session.messages.length <= 1 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s} onClick={() => send(s)}
                      className="pf-chip pf-chip-lav hover:pf-chip-mint text-[11px] cursor-pointer"
                      data-testid={`ai-suggest-${s.slice(0, 10)}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 py-4 border-t border-white/60 flex gap-2">
          <input value={input}
                 onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && !sending && send(input)}
                 placeholder={draft.title ? "Ask AI to change something…" : "Describe your promotion…"}
                 disabled={sending}
                 className="pf-input flex-1 px-4 py-2.5 text-sm"
                 data-testid="ai-input" />
          <button onClick={() => send(input)} disabled={sending || !input.trim()}
                  className="pf-btn-primary px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60"
                  data-testid="ai-send-btn">
            <Send className="w-4 h-4 inline" />
          </button>
        </div>
      </div>

      <div className="pf-card p-5 h-fit md:sticky md:top-24" data-testid="ai-draft-panel">
        <div className="pf-chip pf-chip-cyan mb-2">Draft</div>
        <h3 className="font-display text-xl text-slate-900">Review your coupon</h3>
        <p className="text-xs text-slate-500 mt-1">Nothing is published until you tap Publish.</p>

        <dl className="mt-4 space-y-3 text-sm">
          <Row label="Title" value={draft.title} />
          <Row label="Discount" value={draft.discount_type && draft.discount_value ? (
            draft.discount_type === "percent" ? `${draft.discount_value}% OFF` :
            draft.discount_type === "fixed" ? `CA$${draft.discount_value} OFF` :
            draft.discount_type.toUpperCase()) : null} />
          <Row
            label="Scope"
            value={
              (draft.product_ids || []).length > 0
                ? `${(draft.product_ids || []).length} product${(draft.product_ids || []).length === 1 ? "" : "s"}`
                : (draft.category_id ? "Category" : null)
            }
          />
          {(draft.product_ids || []).length > 0 && (
            <Row
              label="Products"
              value={(draft.product_ids || [])
                .map((pid) => {
                  const p = (session.products || []).find((x) => x.id === pid);
                  if (!p) return pid;
                  return p.variant_title ? `${p.name} · ${p.variant_title}` : p.name;
                })
                .join(", ")}
            />
          )}
          {!(draft.product_ids || []).length && (
            <Row label="Category" value={draft.category_id} mono />
          )}
          <Row label="Starts" value={fmt(draft.starts_at)} />
          <Row label="Expires" value={fmt(draft.expires_at)} />
          <Row label="Provinces" value={(draft.provinces || []).join(", ") || null} />
          <Row label="Cities" value={(draft.cities || []).join(", ") || null} />
          {draft.online_only && <div className="pf-chip pf-chip-mint">Online only</div>}
        </dl>

        {needs.length > 0 && (
          <div className="mt-4 rounded-xl bg-amber-50/70 border border-amber-200 p-3 text-xs text-amber-800" data-testid="ai-needs">
            Still needed: {needs.join(", ")}
          </div>
        )}

        {changeChips.length > 0 && (
          <div className="mt-4 rounded-xl bg-cyan-50/50 border border-cyan-100 p-3" data-testid="ai-change-chips">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-800 font-semibold">
              <Wand2 className="w-3.5 h-3.5" /> Ask AI to change
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {changeChips.map((c) => (
                <button
                  key={c}
                  onClick={() => askChange(c)}
                  disabled={sending}
                  className="pf-chip pf-chip-cyan text-[11px] hover:pf-chip-mint cursor-pointer disabled:opacity-50"
                  data-testid={`ai-change-chip-${c.slice(0, 12).replace(/\s+/g, "-")}`}
                  title={`Fill the chat with: "${c}"`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Or just type any change (e.g. "add free shipping") in the chat.
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2">
          <button onClick={publish} disabled={!ready || sending}
                  className="pf-btn-primary py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                  data-testid="ai-publish-btn">
            <CheckCircle2 className="w-4 h-4" /> Publish coupon
          </button>
          <button onClick={editManually} disabled={!draft.title}
                  className="pf-btn-glass py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50"
                  data-testid="ai-edit-manually-btn">
            <Edit3 className="w-4 h-4" /> Edit manually
          </button>
          <button onClick={saveDraft} disabled={!draft.title}
                  className="pf-btn-glass py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50"
                  data-testid="ai-save-draft-btn">
            <Save className="w-4 h-4" /> Save draft
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</dt>
      <dd className={`text-slate-900 text-right max-w-[60%] break-words ${mono ? "font-mono text-xs" : "text-sm"}`}>
        {value || <span className="text-slate-400 italic text-xs">—</span>}
      </dd>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}
